import { useState, useRef } from 'react';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import FormHelperText from '@mui/material/FormHelperText';
import { alpha } from '@mui/material/styles';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import PersonIcon from '@mui/icons-material/Person';
import { uploadService } from '@/services/upload.service';
import { DocumentType } from '@/types';

interface ProfilePhotoUploadProps {
  value?: string;
  onChange: (url: string) => void;
  error?: string;
  /** Fills the parent column — use in side-by-side desktop layouts */
  fillHeight?: boolean;
}

export function ProfilePhotoUpload({ value, onChange, error, fillHeight = false }: ProfilePhotoUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadError('');
    setUploading(true);

    try {
      const result = await uploadService.uploadDocument(file, DocumentType.PROFILE_PHOTO);
      onChange(result.url);
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Upload failed. Please try again.';
      setUploadError(msg);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: fillHeight ? 'space-between' : 'flex-start',
        gap: 1.5,
        width: '100%',
        height: fillHeight ? '100%' : 'auto',
        minHeight: fillHeight ? { md: 280 } : 'auto',
        p: fillHeight ? { xs: 0, md: 2 } : 0,
        border: fillHeight ? { xs: 'none', md: '2px dashed' } : 'none',
        borderColor: 'divider',
        borderRadius: fillHeight ? 2 : 0,
        bgcolor: fillHeight ? { xs: 'transparent', md: 'backgrounds.sectionAlt' } : 'transparent',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          flex: fillHeight ? { md: 1 } : '0 0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: fillHeight ? { md: 160 } : 'auto',
        }}
      >
        {fillHeight && value ? (
          <Box
            component="img"
            src={value}
            alt="Profile preview"
            sx={{
              width: '100%',
              height: '100%',
              maxHeight: { xs: 120, md: 220 },
              objectFit: 'cover',
              borderRadius: 2,
              border: '3px solid',
              borderColor: error ? 'error.main' : 'secondary.main',
            }}
          />
        ) : (
          <Avatar
            src={value}
            sx={{
              width: fillHeight ? { xs: 100, md: '100%' } : { xs: 100, sm: 120 },
              height: fillHeight ? { xs: 100, md: '100%' } : { xs: 100, sm: 120 },
              maxWidth: fillHeight ? { md: 220 } : 'none',
              maxHeight: fillHeight ? { md: 220 } : 'none',
              aspectRatio: fillHeight ? { md: '1' } : 'auto',
              border: '3px solid',
              borderColor: error ? 'error.main' : 'secondary.main',
            }}
          >
            {!value && <PersonIcon sx={{ fontSize: fillHeight ? { xs: 56, md: 72 } : 56 }} />}
          </Avatar>
        )}

        {uploading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: (theme) => alpha(theme.palette.common.black, 0.5),
              borderRadius: fillHeight ? 2 : '50%',
            }}
          >
            <CircularProgress size={32} sx={{ color: 'white' }} />
          </Box>
        )}

        <Box
          component="label"
          htmlFor="profile-photo-input"
          sx={{
            position: 'absolute',
            bottom: fillHeight ? { xs: 0, md: 8 } : 0,
            right: fillHeight ? { xs: 0, md: 8 } : 0,
            bgcolor: 'secondary.main',
            color: 'primary.contrastText',
            borderRadius: '50%',
            width: 32,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: 2,
            '&:hover': { bgcolor: 'secondary.dark' },
          }}
        >
          <CameraAltIcon sx={{ fontSize: 16 }} />
        </Box>
      </Box>

      <input
        id="profile-photo-input"
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        capture="user"
        style={{ display: 'none' }}
        onChange={handleFileChange}
        disabled={uploading}
      />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.75,
          width: '100%',
          flexShrink: 0,
        }}
      >
        <Button
          variant="outlined"
          size="small"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          fullWidth={fillHeight}
          sx={{ maxWidth: fillHeight ? 220 : 'none' }}
        >
          {value ? 'Change Photo' : 'Upload Photo'}
        </Button>

        {(error || uploadError) && (
          <FormHelperText error sx={{ m: 0, textAlign: 'center' }}>
            {uploadError || error}
          </FormHelperText>
        )}

        <Typography variant="caption" color="text.secondary" textAlign="center">
          JPG, PNG or WEBP. Max 10MB.
        </Typography>
      </Box>
    </Box>
  );
}
